// =============================================================================
// SIREPPAR - Qué tiene que hacer el Secretario Electoral en cada expediente
// =============================================================================
// La bandeja de Resolución (/gestion?tab=resolucion) mostraba en Alertas los
// contadores de observaciones y nada más: el Secretario tenía que abrir cada
// expediente para saber si le tocaba elevar al TEP, registrar un acta o resolver.
//
// Esto deriva la acción pendiente del tipo de trámite + estado + fase, para
// mostrarla como TEXTO PLANO (sin color): no es una alerta, es la tarea. Los
// badges de color de esa columna quedan para lo que sí es urgente.
// =============================================================================

import { getFaseActual } from './fasesReconocimiento';
import { getNoPresentado } from './noPresentado';

interface ExpedienteMinimo {
  tipo: string;
  estado: string;
  datosFormulario?: unknown;
}

// Fase del reconocimiento → qué le falta hacer al Secretario.
const POR_FASE_RECONOCIMIENTO: Record<string, string> = {
  // La SEP verifica los atributos, pero publicar es un acto del Secretario (Art. 10).
  ATRIBUTOS_A_PUBLICAR: 'Para publicar los atributos (oposiciones)',
  ATRIBUTOS_EN_SECRETARIO: 'Para elevar atributos al TEP',
  ATRIBUTOS_EN_TEP: 'Para registrar la resolución del TEP (atributos)',
  DOCUMENTACION_EN_SECRETARIO: 'Para elevar la Documentación al TEP',
  DOCUMENTACION_EN_TEP: 'Para registrar la resolución del TEP (documentación)',
  DEFINITIVO_EN_SECRETARIO: 'Para elevar el Rec. Definitivo al TEP',
  DEFINITIVO_EN_TEP: 'Para registrar la resolución del TEP (definitivo)',
};

/**
 * Texto de la acción pendiente del Secretario, o null si en ese expediente no
 * tiene nada que hacer (p. ej. sigue en manos de la SEP o del partido).
 */
export function accionPendienteSecretario(exp: ExpedienteMinimo): string | null {
  // "No presentado" primero: es un circuito propio que se superpone al estado.
  if (exp.estado === 'NO_PRESENTADO') {
    const np = getNoPresentado(exp.datosFormulario);
    if (np?.subFase === 'EN_TEP') return 'No presentó en términos — en el TEP';
    if (np?.subFase === 'EN_SECRETARIO') return 'No presentó en términos';
    return null;
  }

  if (exp.tipo === 'RECONOCIMIENTO') {
    const fase = getFaseActual(exp.datosFormulario);
    return (fase && POR_FASE_RECONOCIMIENTO[fase]) || null;
  }

  if (exp.tipo === 'REEMPADRONAMIENTO') {
    // PENDIENTE_RESOLUCION es el nombre anterior de EN_SECRETARIO.
    if (exp.estado === 'EN_SECRETARIO' || exp.estado === 'PENDIENTE_RESOLUCION') {
      // El Secretario RESUELVE: aprueba con su propia resolución si el expediente
      // está correcto, y solo eleva al TEP los que no lo están. Antes decía
      // "Confirmar reempadronamiento", que no distinguía las dos salidas.
      return 'Para resolver el reempadronamiento (o elevar al TEP)';
    }
    if (exp.estado === 'EN_TEP') return 'Para registrar la resolución del TEP';
    return null;
  }

  // Constitución de alianza: la resuelve el TEP, sí o sí. El Secretario no dicta
  // resolución, sólo eleva; por eso la tarea que se muestra es la elevación y no
  // "resolver", que lo dejaba esperando una decisión que no le corresponde tomar.
  if (exp.tipo === 'CONSTITUCION_ALIANZA') {
    if (exp.estado === 'EN_SECRETARIO' || exp.estado === 'PENDIENTE_RESOLUCION') {
      return 'Para elevar la alianza al TEP';
    }
    if (exp.estado === 'EN_TEP') return 'Para registrar la resolución del TEP';
    return null;
  }

  // Resto de los trámites (rendiciones, cambio de autoridades, fusión…): el
  // Secretario resuelve directo con Aprobar / Rechazar.
  if (exp.estado === 'EN_SECRETARIO' || exp.estado === 'PENDIENTE_RESOLUCION') {
    return 'Para resolver el expediente';
  }
  return null;
}
